import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';

/**
 * PollVotingOptions: Handles rendering the different interaction modes (single, multiple, rating, tiles).
 * SELECTION ONLY - TAP TO SELECT. This component never calls the backend or actually casts a
 * vote — it only tracks which option(s) are locally selected via `handleTileClick`, which the
 * parent (PollCard) uses to update its own `selection` state. The real vote is cast later, when
 * the user confirms via SlideToVote.
 *
 * `options` elements may be either plain strings or `{ label, img }` objects — `getLabel()`
 * normalizes both. `selection` mirrors that: a single string label for single/binary/rating
 * polls, or an array of labels for multi-select/rank polls (`isMultiSelect`).
 */
const PollVotingOptions = ({ type, options, selection, handleTileClick, inModal, showResults = false, results = [], leadingIndex = -1, compact = false }) => {
    const isMultiSelect = type === 'multiple' || type === 'rank';

    const getLabel = (opt) => typeof opt === 'object' ? opt.label : opt;

    // Intelligent Grid Logic - Mirror UI (Same for Card and Modal)
    const maxOptionLength = options.length > 0 
        ? Math.max(...options.map(opt => getLabel(opt).length)) 
        : 0;
    const canFitTwoColumns = maxOptionLength < 25 && options.length > 2;

    const renderOption = (opt, i, variant = 'list') => {
        const label = getLabel(opt);
        const isActive = isMultiSelect ? (selection && selection.includes(label)) : selection === label;
        const percent = results[i] || 0;
        const isWinner = i === leadingIndex && percent > 0;

        if (variant === 'tile') {
            return (
                <motion.button
                    key={i}
                    disabled={showResults}
                    onClick={() => handleTileClick(label)}
                    whileHover={!showResults ? { scale: 1.02 } : {}}
                    whileTap={!showResults ? { scale: 0.98 } : {}}
                    className={`h-full w-full rounded-[20px] flex flex-col items-center justify-center border border-gray-200/60 dark:border-white/10 relative overflow-hidden transition-all duration-300 group/tile ${showResults ? 'cursor-default' : 'hover:scale-[1.02] hover:border-primary-600/40 hover:bg-gray-50/50 dark:hover:border-primary-600/40 dark:hover:bg-white/[0.04] hover:shadow-2xl hover:shadow-purple-500/20 active:scale-[0.98]'} ${isActive && !showResults
                        ? 'bg-gradient-to-br from-primary-600/20 to-primary-700/20 border-primary-600 shadow-xl shadow-purple-500/10 text-primary-600 dark:text-white'
                        : 'bg-white dark:bg-white/[0.02] text-gray-800 dark:text-gray-200'
                        }`}
                >
                    {showResults && (
                        <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${percent}%` }}
                            transition={{
                                type: "spring",
                                stiffness: 40,
                                damping: 15,
                                mass: 1
                            }}
                            className={`absolute inset-0 ${isWinner ? 'bg-primary-600/20' : 'bg-gray-100 dark:bg-gray-800/50'}`}
                        />
                    )}
                    <div className="relative z-10 flex flex-col items-center">
                        <span className="text-sm font-black uppercase tracking-[0.1em] select-none italic text-center px-4 leading-tight transition-colors duration-300 group-hover/tile:text-primary-600">{label}</span>
                        {showResults ? <span className="text-xl font-black tabular-nums mt-1">{percent}%</span> : isActive && <div className="mt-1.5 w-2 h-2 rounded-full bg-primary-600 animate-pulse" />}
                    </div>
                    {showResults && isWinner && <div className="absolute top-2 right-2 px-2 py-0.5 bg-primary-600 text-white text-2xs font-black uppercase tracking-widest rounded-full shadow-lg z-20">Winner</div>}
                </motion.button>
            );
        }

        if (variant === 'rating') {
            const isHighlighted = !isNaN(parseInt(selection)) && parseInt(label) <= parseInt(selection);
            const isSelected = label === selection;

            return (
                <motion.button
                    key={i}
                    disabled={showResults}
                    onClick={() => handleTileClick(label)}
                    whileHover={!showResults ? { scale: 1.2 } : {}}
                    whileTap={!showResults ? { scale: 0.9 } : {}}
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-sm border transition-all duration-200 ${isHighlighted
                        ? 'bg-primary-600 border-primary-600 text-white shadow-xl shadow-purple-500/40 ' + (isSelected ? 'ring-4 ring-purple-100 dark:ring-purple-900/20 scale-110' : 'opacity-80')
                        : 'bg-white dark:bg-white/[0.02] border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400'
                        }`}
                >
                    {label}
                </motion.button>
            );
        }

        return (
            <motion.button
                key={i}
                disabled={showResults}
                onClick={() => handleTileClick(label)}
                whileHover={!showResults ? { scale: 1.01 } : {}}
                whileTap={!showResults ? { scale: 0.99 } : {}}
                className={`w-full p-4.5 min-h-[72px] text-left rounded-[20px] font-bold flex items-center justify-between border relative overflow-hidden transition-all duration-500 group/list ${showResults ? 'cursor-default' : 'hover:border-primary-600/40 hover:bg-gray-50/50 dark:hover:border-primary-600/40 dark:hover:bg-white/[0.04] hover:shadow-xl active:scale-[0.99]'} ${isActive && !showResults
                    ? 'border-primary-600 bg-primary-600/5 dark:bg-primary-600/10 shadow-lg shadow-purple-500/5 z-10 text-primary-600 dark:text-white'
                    : 'border-gray-200/60 dark:border-white/10 bg-white dark:bg-white/[0.02] text-gray-800 dark:text-gray-200'
                    }`}
            >
                {showResults && (
                    <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${percent}%` }}
                        transition={{
                            type: "spring",
                            stiffness: 40,
                            damping: 15,
                            mass: 1
                        }}
                        className={`absolute inset-0 ${isWinner ? 'bg-primary-600/15' : 'bg-gray-100/50 dark:bg-white/5'}`}
                    />
                )}
                <div className="relative z-10 flex items-center justify-center w-full">
                    <div className="flex items-center justify-center w-full">
                        <span className="text-sm font-black line-clamp-2 italic text-center transition-colors duration-300 group-hover/list:text-primary-600 dark:group-hover/list:text-white">{label}</span>
                        {showResults && isWinner && <span className="absolute left-2 top-0 px-2 py-0.5 bg-primary-600 text-white text-2xs font-black uppercase tracking-widest rounded-full shadow-sm">Winner</span>}
                    </div>
                    {showResults ? <span className="text-xl font-black tabular-nums drop-shadow-sm">{percent}%</span> : isActive && <div className="absolute right-4 w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center text-white shadow-lg"><CheckCircle2 size={12} /></div>}
                </div>
            </motion.button>
        );
    };

    return (
        <div className={`flex flex-wrap justify-center gap-4`}>
            {/* All Options - Unified Style Mapping */}
            {options.map((opt, i) => (
                <div key={i} className={`${type === 'rating' ? '' : canFitTwoColumns ? 'w-[calc(50%-8px)]' : 'w-full'} min-w-[40px]`}>
                    {renderOption(opt, i, type === 'rating' ? 'rating' : 'list')}
                </div>
            ))}
        </div>
    );
};

export default PollVotingOptions;
